import React, { useState, useRef, useEffect } from 'react'
import './DeclaForm.css'
import { generateDeclarationPDF } from '../../utils/pdfHandler'
import sendEmail from '../../utils/emailHandler'
import 'jspdf-autotable'
import { categoryOptions } from '../../data/categories'
import { companyRecipients } from '../../data/companyRecipients'
import FileInputField from '../InputField/FileInputField'
import SelectedFiles from '../InputField/SelectedFiles'
import InputField from '../InputField/InputField'
import SelectField from '../InputField/SelectField'
import useGraphData from '../../hooks/graph'
import Modal from '../Modal/Modal'

function DeclaForm() {
    const { userName, mail } = useGraphData()
    const [name, setName] = useState('')
    const [bankAccountNumber, setBankAccountNumber] = useState('')
    const [recipient, setRecipient] = useState('')
    const [posts, setPosts] = useState([
        {
            id: 1,
            description: '',
            category: '',
            totalAmountWithoutTaxes: '',
            taxAmount: '',
        },
    ])
    const fileInput = useRef()
    const [selectedFiles, setSelectedFiles] = useState([])
    const categorySelectRefs = useRef([])
    const newPostRef = useRef(null)

    const dateToday = new Date().toLocaleDateString('nl-NL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    })

    const [isModalOpen, setIsModalOpen] = useState(false)
    const [modalMessage, setModalMessage] = useState('')
    const [modalCallback, setModalCallback] = useState(null)
    const [modalCancelCallback, setModalCancelCallback] = useState(null)
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        if (userName) {
            setName(userName)
        }
    }, [userName])

    useEffect(() => {
        if (!mail) return

        const domain = mail.split('@')[1]
        switch (domain) {
            case 'newspark.nl':
                setRecipient('rp@newspark.nl')
                break
            case 'd2win.nl':
                setRecipient('administratie@d2win.nl')
                break
            case 'acda-rpa.nl':
                setRecipient('administratie@acda-rpa.nl')
                break
            case 'bluenexus.nl':
                setRecipient('administratie@bluenexus.nl')
                break
            case 'blynkt.nl':
                setRecipient('administratie@blynkt.nl')
                break
            case 'everybodyfrank.com':
                setRecipient('diederick.doets@everybodyfrank.com')
                break
            default:
                setRecipient(null)
                break
        }
    }, [mail])

    const handlePostChange = (index, field, value) => {
        const newPosts = [...posts]
        newPosts[index][field] = value
        setPosts(newPosts)
    } 

    const handleSubmit = async (e) => {
        e.preventDefault();
    
        if (selectedFiles.length === 0) {
            setModalMessage('Upload alstublieft minstens één bestand.');
            setModalCallback(null);
            setModalCancelCallback(() => () => setIsModalOpen(false));
            setIsModalOpen(true);
            return;
        }
    
        const validatePosts = async (index = 0) => {
            if (index >= posts.length) {
                proceedWithSubmission(); // All posts validated, proceed with submission
                return;
            }
    
            const post = posts[index];
            if (post.category !== '4505' && post.taxAmount === '') {
                setModalMessage(
                    `Het BTW bedrag van post ${index + 1} is niet ingevuld. Wilt u toch doorgaan?`
                );
                setModalCallback(() => {
                    return () => validatePosts(index + 1); 
                });
                setModalCancelCallback(() => {
                    return () => setIsModalOpen(false); 
                });
                setIsModalOpen(true);
            } else {
                validatePosts(index + 1); 
            }
        };
    
        validatePosts(); // Start validating posts
    };

    const proceedWithSubmission = async () => {
        const totalAmount = posts.reduce(
            (acc, post) =>
                acc +
                Number(post.totalAmountWithoutTaxes) +
                Number(post.taxAmount),
            0
        )

        const files = fileInput.current.files

        generateAndSendPDF(null, totalAmount, files)
    }

    const generateAndSendPDF = async (newTab, totalAmount, files) => {
        setIsLoading(true)
        const pdfData = generateDeclarationPDF(
            name,
            posts,
            categorySelectRefs,
            dateToday,
            totalAmount
        )

        const response = await sendEmail(
            name,
            mail,
            bankAccountNumber,
            recipient,
            totalAmount,
            dateToday,
            selectedFiles,
            pdfData,
            'declaration'
        )

        if (response) {
            setPosts([
                {
                    id: 1,
                    description: '',
                    category: '',
                    totalAmountWithoutTaxes: '',
                    taxAmount: '',
                },
            ])
            setSelectedFiles([])
            setModalMessage('Declaratie succesvol verzonden!')
            setModalCallback(null)
            setModalCancelCallback(() => () => setIsModalOpen(false))
            setIsModalOpen(true)
        }
        setIsLoading(false)
    }

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files)
        const validTypes = ['application/pdf', 'image/jpeg', 'image/png']

        const invalidFiles = files.filter(
            (file) => !validTypes.includes(file.type)
        )
        if (invalidFiles.length > 0) {
            setModalMessage(
                'Upload alstjeblieft alleen pdf of image-bestanden (JPEG, PNG).'
            )
            setModalCallback(null)
            setModalCancelCallback(() => () => setIsModalOpen(false))
            setIsModalOpen(true)
            return
        }

        setSelectedFiles((prevFiles) => [...prevFiles, ...files])
        e.target.value = null
    }

    const handleFileDelete = (index) => {
        setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index))
    }

    const addPost = () => {
        setPosts((prevPosts) => {
            const newPosts = [
                ...prevPosts,
                {
                    id: prevPosts.length + 1,
                    description: '',
                    category: '',
                    totalAmountWithoutTaxes: '',
                    taxAmount: '',
                },
            ]
            return newPosts
        })
    
        // Dynamically add a new ref for the new post
        categorySelectRefs.current.push(React.createRef())
    }

    useEffect(() => {
        if (posts.length > 1 && newPostRef.current) {
            newPostRef.current.scrollIntoView({ behavior: 'smooth' })
        }
    }, [posts])

    const removePost = () => {
        setPosts(posts.slice(0, -1))
    }

    return (
        <div>
            <h2>Declaratie Formulier</h2>
            <form onSubmit={handleSubmit}>
                <div className="form-section">
                    <InputField
                        label="Naam declarant"
                        type="text"
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                    />
                    <InputField
                        label="Bankrekeningnummer (optioneel)"
                        type="text"
                        id="bankAccountNumber"
                        value={bankAccountNumber}
                        onChange={(e) => setBankAccountNumber(e.target.value)}
                    />
                    <SelectField
                        label="BV"
                        type="text"
                        id="bv"
                        value={recipient}
                        onChange={(e) => setRecipient(e.target.value)}
                        required
                        options={companyRecipients}
                    />
                    <FileInputField
                        id="fileInput"
                        fileInput={fileInput}
                        handleFileChange={handleFileChange}
                    />
                    <SelectedFiles
                        selectedFiles={selectedFiles}
                        handleFileDelete={handleFileDelete}
                    />
                </div>
                {posts.map((post, index) => (
                    <div
                        key={post.id}
                        ref={index === posts.length - 1 ? newPostRef : null}
                        className="form-section"
                    >
                        <h3>Post {index + 1}</h3>
                        <InputField
                            label="Omschrijving"
                            type="text"
                            id={`description${index}`}
                            value={post.description}
                            onChange={(e) =>
                                handlePostChange(
                                    index,
                                    'description',
                                    e.target.value
                                )
                            }
                            required
                            maxLength={50}
                        />
                        <SelectField
                            label="Categorie"
                            id={`category${index}`}
                            value={post.category}
                            onChange={(e) =>
                                handlePostChange(
                                    index,
                                    'category',
                                    e.target.value
                                )
                            }
                            required
                            options={categoryOptions}
                            ref={categorySelectRefs.current[index]}
                        />
                        <InputField
                            label="Bedrag (excl. BTW)"
                            type="text"
                            id={`totalAmountWithoutTaxes${index}`}
                            value={post.totalAmountWithoutTaxes
                                .toString()
                                .replace('.', ',')}
                            onChange={(e) => {
                                const formattedValue = e.target.value.replace(
                                    ',',
                                    '.'
                                )
                                handlePostChange(
                                    index,
                                    'totalAmountWithoutTaxes',
                                    formattedValue
                                )
                            }}
                            required
                            allowedChars={'0-9.,'}
                        />
                        <InputField
                            label="BTW bedrag"
                            type="text"
                            id={`taxAmount${index}`}
                            value={post.taxAmount.toString().replace('.', ',')}
                            onChange={(e) => {
                                const formattedValue = e.target.value.replace(
                                    ',',
                                    '.'
                                )
                                handlePostChange(
                                    index,
                                    'taxAmount',
                                    formattedValue
                                )
                            }}
                            allowedChars={'0-9.,'}
                        />
                    </div>
                ))}
                {posts.length > 1 && (
                    <button type="button" onClick={removePost}>
                        Haal laatste post weg
                    </button>
                )}
                {posts.length < 10 && (
                    <button type="button" onClick={addPost}>
                        Voeg extra post toe
                    </button>
                )}
                <button type="submit" disabled={isLoading}>
                    {isLoading ? 'Verzenden...' : 'Opsturen'}
                </button>{' '}
            </form>
            <Modal
                isOpen={isModalOpen}
                message={modalMessage}
                onConfirm={modalCallback}
                onCancel={modalCancelCallback}
                isDisabled={isLoading}
            />
        </div>
    )
}

export default DeclaForm
